import { useQuery } from '@tanstack/react-query';
import { formatEther } from 'viem';
import { INK_DEPOSIT_CHAIN } from '../config/chains';
import { getSourceChainPublicClient } from '../utils/getSourceChainPublicClient';

export interface InkGasBalanceData {
  /** Raw ETH balance in wei */
  raw: bigint;
  /** Human-readable ETH balance string */
  formatted: string;
}

/**
 * Reads the ETH (native gas) balance for an address on the Ink chain.
 *
 * Uses a dedicated publicClient pointed at Ink RPC — NOT the wagmi-connected wallet chain.
 * This allows reading the Ink balance even when the user's wallet is on a different chain.
 *
 * @param address - The address to check the Ink ETH balance for
 */
export function useInkGasBalance(address: `0x${string}` | undefined) {
  return useQuery<InkGasBalanceData>({
    queryKey: ['inkGasBalance', address],
    queryFn: async () => {
      if (!address) {
        throw new Error('address is required');
      }

      const publicClient = getSourceChainPublicClient(INK_DEPOSIT_CHAIN.chainId);

      const raw = await publicClient.getBalance({ address });

      return {
        raw,
        formatted: formatEther(raw),
      };
    },
    enabled: !!address,
    staleTime: 30_000,
  });
}
